import { debounce } from 'lodash-es';
import { BUILD } from '../build';
import { FullscreenHelper } from '../helpers/FullscreenHelper';
import { Config } from './Config';
import { EventBus } from './EventBus';
import { LinkService } from './LinkService';
import { PDFJSLinkService } from './PDFJSLinkService';
import { RenderingQueue } from './RenderingQueue';
import { TokenService } from './TokenService';
import { Viewer } from './Viewer';
import { ViewerEventType, ViewerFullscreenChangeEvent } from '../types/ViewerEvents.type';
import { PdfViewerPresentationRenderer } from './renderers/PdfViewerPresentationRenderer';
import { AssetViewerPresentationRenderer } from './renderers/AssetViewerPresentationRenderer';
import { AssetViewerItemPresentationsPreloaderService } from '../services/asset-viewer-item-presentations-preloader.service';
import { NavigationComponent } from './navigation/navigation.component';
import { KeyCodeName } from '../types/key-code-name.type';
import { SwipeService } from '../services/swipe.service';
export class AssetViewer {
    constructor(assetViewerItems, config) {
        this.assetViewerItems = assetViewerItems;
        this.config = config;
        this.onKeyDownHandler = (event) => {
            if (!this.viewer.active) {
                return;
            }
            this.handleKeyboardEvent(event);
        };
        this.config = new Config(config);
        this.eventBus = new EventBus();
        this.tokenService = new TokenService(this.config);
        // TODO remove or make full use of it
        this.renderingQueue = new RenderingQueue(this.config);
        this.pdfJsLinkService = new PDFJSLinkService(this.eventBus);
        this.container = this.config.container;
        this.thumbnailContainer = this.config.thumbnailContainer;
        this.debouncedResize = debounce(this.onResize.bind(this), this.config.RESIZE_DEBOUNCE_TIMEOUT);
        this.toggleFullscreen = this.toggleFullscreen.bind(this);
        this.onFullscreenChange = this.onFullscreenChange.bind(this);
        this.build = BUILD;
        if (this.config.IS_SLAVE_ASSET_VIEWER === false) {
            this.config.rootAssetViewer = this;
        }
        this.viewer = new Viewer(this.config, this.eventBus, this.assetViewerItems, this.tokenService, this.renderingQueue, false, undefined, this.pdfJsLinkService);
        this.renderingQueue.setViewer(this.viewer);
        this.renderingQueue.onIdle = this.viewer.onIdle;
        if (this.config.navigationContainer) {
            this.navigationComponent = new NavigationComponent(this.config.THEME, this.config.THEME_FULLSCREEN, this.config.navigationContainer, this);
            this.navigationComponent.init();
        }
        this.assetViewerItemPresentationsPreloaderService = new AssetViewerItemPresentationsPreloaderService(this.assetViewerItems, this.eventBus);
        if (this.config.thumbnailViewer) {
            const thumbnailConfig = new Config(Object.assign({}, this.config, {
                ENABLE_LINKS: false,
                CAN_CREATE_ANNOTATIONS: false,
                SHOW_VIEW_LOADER: false,
                VIEWER_LAYOUT: this.config.THUMBNAIL_VIEWER_LAYOUT,
                VIEW_BORDER: this.config.THUMBNAIL_VIEW_BORDER,
                VIEW_PADDING_TOP: this.config.THUMBNAIL_VIEW_PADDING_TOP,
                VIEW_PADDING_BOTTOM: this.config.THUMBNAIL_VIEW_PADDING_BOTTOM,
                VIEW_PADDING_LEFT: this.config.THUMBNAIL_VIEW_PADDING_LEFT,
                VIEW_PADDING_RIGHT: this.config.THUMBNAIL_VIEW_PADDING_RIGHT,
                VIEW_BORDER_BOTTOM: this.config.THUMBNAIL_VIEW_BORDER_BOTTOM,
                PRESENTATION_RENDERERS_FOR_ASSET_TYPE_MAP: this.config.THUMBNAIL_PRESENTATION_RENDERERS_FOR_ASSET_TYPE_MAP,
                IS_SLAVE_ASSET_VIEWER: false
            }));
            this.thumbnailViewerEventBus = new EventBus();
            this.linkService = new LinkService(this.viewer, this.eventBus);
            this.thumbnailViewer = new Viewer(thumbnailConfig, this.thumbnailViewerEventBus, this.assetViewerItems, this.tokenService, this.renderingQueue, true, this.linkService, null);
            this.renderingQueue.setThumbnailViewer(this.thumbnailViewer);
        }
    }
    init(assetIndexToFocus, page, container, thumbnailContainer, selectViewsEnabled, viewsToSelect) {
        const containerElement = container || this.container;
        if (!this.config.IS_SLAVE_ASSET_VIEWER) {
            this.fullscreenContainer = containerElement.parentElement;
            this.viewer.viewerLayout.fullscreenContainer = this.fullscreenContainer;
        }
        if (!this.viewer) {
            throw 'viewer was destroyed';
        }
        this.addEventListeners();
        this.viewer.init(containerElement, assetIndexToFocus, page);
        if (selectViewsEnabled) {
            this.enableViewSelection();
            if (viewsToSelect && viewsToSelect.length) {
                this.selectViews(viewsToSelect);
            }
        }
        if (this.config.SWIPE_ENABLED) {
            this.swipeService = new SwipeService();
            this.swipeService.init(this);
        }
    }
    initThumbnailViewer(thumbnailContainer) {
        const thumbnailContainerElement = thumbnailContainer || this.thumbnailContainer;
        if (this.thumbnailViewer && this.thumbnailContainer) {
            this.thumbnailViewer.init(thumbnailContainerElement, undefined, undefined);
        }
    }
    destroy() {
        this.removeEventListeners();
        if (this.navigationComponent) {
            this.navigationComponent.destroy();
            this.navigationComponent = null;
        }
        if (this.thumbnailViewer) {
            this.thumbnailViewer.destroy();
            this.thumbnailViewer = null;
        }
        if (this.assetViewerItemPresentationsPreloaderService) {
            this.assetViewerItemPresentationsPreloaderService.destroy();
            this.assetViewerItemPresentationsPreloaderService = null;
        }
        if (this.swipeService) {
            this.swipeService.destroy();
            this.swipeService = null;
        }
        this.viewer.destroy();
        this.container = null;
        this.thumbnailContainer = null;
        this.config = null;
        this.viewer = null;
        this.linkService = null;
        this.renderingQueue = null;
        this.debouncedResize = null;
    }
    /*
     * Adds eventlisteners and starts the update cycle
     */
    setActive() {
        if (this.viewer) {
            this.viewer.setActive();
            if (this.thumbnailViewer) {
                this.renderingQueue.thumbnailViewerActive = true;
                this.thumbnailViewer.setActive();
            }
        }
    }
    /*
     * Removes eventlisteners, no view/viewport updates happen while in passive mode
     */
    setPassive() {
        if (this.viewer) {
            this.viewer.setPassive();
            if (this.thumbnailViewer) {
                this.renderingQueue.thumbnailViewerActive = false;
                this.thumbnailViewer.setPassive();
            }
        }
    }
    addEventListeners() {
        if (!this.config.IS_SLAVE_ASSET_VIEWER) {
            window.addEventListener('resize', this.debouncedResize);
            this.eventBus.on(ViewerEventType.ToggleFullscreen, this.toggleFullscreen);
            FullscreenHelper.addFullscreenChangeListener(this.onFullscreenChange);
            document.addEventListener('keydown', this.onKeyDownHandler);
        }
    }
    removeEventListeners() {
        if (!this.config.IS_SLAVE_ASSET_VIEWER) {
            window.removeEventListener('resize', this.debouncedResize);
            this.eventBus.off(ViewerEventType.ToggleFullscreen, this.toggleFullscreen);
            FullscreenHelper.removeFullscreenChangeListener(this.onFullscreenChange);
            document.removeEventListener('keydown', this.onKeyDownHandler);
        }
    }
    onResize() {
        // Don't resize when you use the fullscreen button of a element video element
        if (FullscreenHelper.isFullscreen() &&
            this.fullscreenContainer &&
            FullscreenHelper.getFullscreenElement() !== this.fullscreenContainer) {
            return;
        }
        this.resize();
        // TODO don't resize thumbnail viewer when container didn't change size?
        if (this.config && this.config.thumbnailViewer) {
            this.thumbnailViewer.resize();
        }
    }
    // trigger resize for viewer and nested viewers
    resize() {
        if (this.viewer) {
            this.viewer.resize();
        }
    }
    canNavigateToNext() {
        return this.viewer ? this.viewer.canNavigateToNext() : false;
    }
    next() {
        if (!this.canNavigateToNext() || this.confirmNavigationToOtherAssetViewerItem) {
            return;
        }
        this.confirmNavigationToOtherAssetViewerItem =
            this.viewer.removePageFromViewId(this.viewer.currentViewId) !== this.viewer.removePageFromViewId(this.viewer.nextViewId)
                ? this.config.confirmNavigationToOtherAssetViewerItem()
                : Promise.resolve(true);
        this.confirmNavigationToOtherAssetViewerItem.then(result => {
            this.confirmNavigationToOtherAssetViewerItem = null;
            if (result) {
                this.viewer.next();
            }
        });
    }
    canNavigateToPrevious() {
        return this.viewer ? this.viewer.canNavigateToPrevious() : false;
    }
    previous() {
        if (!this.canNavigateToPrevious() || this.confirmNavigationToOtherAssetViewerItem) {
            return;
        }
        this.confirmNavigationToOtherAssetViewerItem =
            this.viewer.removePageFromViewId(this.viewer.currentViewId) !== this.viewer.removePageFromViewId(this.viewer.previousViewId)
                ? this.config.confirmNavigationToOtherAssetViewerItem()
                : Promise.resolve(true);
        this.confirmNavigationToOtherAssetViewerItem.then(result => {
            this.confirmNavigationToOtherAssetViewerItem = null;
            if (result) {
                this.viewer.previous();
            }
        });
    }
    navigateToAssetViewerItem(assetViewerItemId, page) {
        if (this.viewer) {
            this.viewer.navigateToAssetViewerItem(assetViewerItemId, page);
        }
    }
    // Handle fullscreen in the Viewer not in the Layout because of complexity with nested Layouts
    toggleFullscreen() {
        if (!this.fullscreenContainer) {
            throw 'Trying to go fullscreen without initializing the AssetViewer';
        }
        if (FullscreenHelper.isFullscreen()) {
            FullscreenHelper.exitFullscreen();
            document.body.classList.remove('fullscreen');
        }
        else {
            FullscreenHelper.enterFullscreen(this.fullscreenContainer);
            document.body.classList.add('fullscreen');
        }
    }
    onFullscreenChange(event) {
        this.eventBus.dispatch(new ViewerFullscreenChangeEvent());
    }
    resetAnnotations() {
        if (this.viewer) {
            this.viewer.resetAnnotations();
        }
        if (this.thumbnailViewer) {
            this.thumbnailViewer.resetAnnotations();
        }
    }
    enableViewSelection() {
        if (this.viewer.currentView) {
            this.viewer.enableNavigationOnlyWithinCurrentAssetViewerItem();
            if (this.viewer.currentView.presentationRenderer instanceof PdfViewerPresentationRenderer) {
                this.viewer.currentView.presentationRenderer.enableViewSelection();
                return;
            }
            const assetViewer = this.viewer.currentView.presentationRenderer.assetViewer || this;
            const thumbnailViewer = assetViewer.thumbnailViewer;
            thumbnailViewer.viewerLayout.enableViewSelection();
        }
    }
    disableViewSelection() {
        if (this.viewer.currentView) {
            this.viewer.disableNavigationOnlyWithinCurrentAssetViewerItem();
            if (this.viewer.currentView.presentationRenderer instanceof PdfViewerPresentationRenderer) {
                this.viewer.currentView.presentationRenderer.disableViewSelection();
                return;
            }
            const assetViewer = this.viewer.currentView.presentationRenderer.assetViewer || this;
            const thumbnailViewer = assetViewer.thumbnailViewer;
            thumbnailViewer.viewerLayout.disableViewSelection();
        }
    }
    selectViews(viewsToSelect) {
        this.viewer.views.forEach(view => {
            if (view.presentationRenderer instanceof PdfViewerPresentationRenderer) {
                view.presentationRenderer.setViewsToSelect(viewsToSelect);
            }
        });
        const assetViewer = this.viewer.currentView.presentationRenderer.assetViewer || this;
        const views = assetViewer.viewer.viewerLayout.views.filter(view => viewsToSelect.find(viewToSelect => viewToSelect.assetViewerItemId === view.assetViewerItem.id && viewToSelect.page === view.page)
            ? true
            : false);
        views.forEach(view => view.selectView());
        if (views && assetViewer.thumbnailViewer) {
            const thumbnailViews = assetViewer.thumbnailViewer.views.filter((view) => views.indexOf(view.linkedView) > -1);
            thumbnailViews.forEach(view => this.setSelectedViewStyle(view));
        }
    }
    selectAllViewsOfCurrentAssetViewerItem(assetViewerItemId) {
        if (assetViewerItemId) {
            if (this.viewer.currentView.presentationRenderer instanceof PdfViewerPresentationRenderer) {
                this.viewer.currentView.presentationRenderer.selectAllViews();
                return;
            }
            const assetViewer = this.viewer.currentView.presentationRenderer.assetViewer || this;
            const views = assetViewer.viewer.views.filter(view => view.assetViewerItem.id === assetViewerItemId);
            const thumbnailViews = assetViewer.thumbnailViewer.views.filter(view => view.assetViewerItem.id === assetViewerItemId);
            views.forEach(view => view.selectView());
            thumbnailViews.forEach(view => this.setSelectedViewStyle(view));
        }
    }
    setSelectedViewStyle(assetViewerView) {
        assetViewerView.styledCheckbox.style.background = this.config.TINT_COLOR;
        assetViewerView.checkboxInput.checked = true;
    }
    handleKeyboardEvent(event) {
        switch (event.key) {
            case KeyCodeName.ArrowRight:
            case KeyCodeName.ArrowRightIE11:
                return this.config.confirmKeyboardNavigation(event) ? this.next() : null;
            case KeyCodeName.ArrowLeft:
            case KeyCodeName.ArrowLeftIE11:
                return this.config.confirmKeyboardNavigation(event) ? this.previous() : null;
        }
    }
    deselectAllViewsOfCurrentAssetViewerItem(assetViewerItemId) {
        if (assetViewerItemId) {
            if (this.viewer.currentView.presentationRenderer instanceof PdfViewerPresentationRenderer) {
                this.viewer.currentView.presentationRenderer.deselectAllViews();
                return;
            }
            const assetViewer = this.viewer.currentView.presentationRenderer.assetViewer || this;
            const views = assetViewer.viewer.views.filter(view => view.assetViewerItem.id === assetViewerItemId);
            const thumbnailViews = assetViewer.thumbnailViewer.views.filter(view => view.assetViewerItem.id === assetViewerItemId);
            views.forEach(view => view.deselectView());
            thumbnailViews.forEach(view => {
                view.styledCheckbox.style.background = 'white';
                view.checkboxInput.checked = false;
            });
        }
    }
    enableNavigationOnlyWithinCurrentAssetViewerItem() {
        if (this.viewer) {
            this.viewer.enableNavigationOnlyWithinCurrentAssetViewerItem();
        }
    }
    disableNavigationOnlyWithinCurrentAssetViewerItem() {
        if (this.viewer) {
            this.viewer.disableNavigationOnlyWithinCurrentAssetViewerItem();
        }
    }
    getAssetViewerViews() {
        return this.viewer.getAssetViewerViews();
    }
    getAssetViewerViewComponentById(id) {
        const assetViewerViews = [];
        this.viewer.views.forEach(assetViewerView => {
            if (assetViewerView.presentationRenderer && assetViewerView.presentationRenderer instanceof AssetViewerPresentationRenderer) {
                assetViewerViews.push(...assetViewerView.presentationRenderer.assetViewer.viewer.views);
            }
            else if (assetViewerView.presentationRenderer &&
                assetViewerView.presentationRenderer instanceof PdfViewerPresentationRenderer) {
                console.warn('getAssetViewerViewComponentById not supported for PdfViewerPresentationRenderer');
            }
            else {
                assetViewerViews.push(assetViewerView);
            }
        });
        return assetViewerViews.find(assetViewerView => assetViewerView.id === id);
    }
}
//# sourceMappingURL=AssetViewer.js.map